import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router';

import { CLONED_MENU_SOURCE } from 'constants/menuManagement';
import { useAuth } from 'hooks/useAuth';
import { LOCATION_PATH, MENU_PATH } from 'constants/path';
import {
  canEditLinkedMerchants,
  getMasterMenuDisabledTooltip,
} from 'helpers/masterMenuPermissions';

import { Alert, Stack, Text, Button } from '@mantine/core';
import MerchantTreeSelect from 'components/MerchantTreeSelect/MerchantTreeSelect';
import { BellIconMd } from '@chownow/pantry/icons';



export default function LinkedMerchants({
  menu,
  selectedLinkedMerchants,
  setSelectedLinkedMerchants,
}) {
  const navigate = useNavigate();
  const {
    cubohUser: { isParentCompanyUser },
    merchants,
  } = useAuth();
  const isMasterMenu = menu?.associatedMerchants?.length > 0;
  const isClonedMenu = menu?.clonedMenu;
  const {
    merchantId: masterMerchantId,
    menuUuid: masterMenuUuid,
    menuName: masterMenuName,
  } = menu?.masterMerchant ?? {};

  useEffect(() => {
    if (menu?.associatedMerchants) {
      setSelectedLinkedMerchants(menu.associatedMerchants);
    }
  }, [menu]);

  const disableMerchantSelector = (() => {
    if (menu?.menuSource === CLONED_MENU_SOURCE || menu?.menuHasSharedCategories) {
      return true;
    }
    if (menu?.masterMenuPermissionTier) {
      return !canEditLinkedMerchants(menu.masterMenuPermissionTier);
    }
    return !isParentCompanyUser;
  })();

  const shouldShowMasterMenuDisabledTooltip =
    disableMerchantSelector &&
    isMasterMenu &&
    !menu?.menuHasSharedCategories &&
    menu?.menuSource !== CLONED_MENU_SOURCE;

  const handleMasterMenuClick = () => {
    const masterMerchant = merchants[masterMerchantId];
    const path = `${LOCATION_PATH}/${masterMerchant.companyID}${MENU_PATH}/edit`;
    const params = `merchantId=${masterMerchantId}&menuId=${masterMenuUuid}`;

    navigate(`${path}?${params}`);
  };

  const alertMessage = (() => {
    if (isMasterMenu) {
      return "This is a master menu. Changes to this menu's items will reflect on all linked merchants.";
    }

    if (isClonedMenu) {
      return (
        <>
          This is a cloned menu. The master for this menu is{' '}
          <Button variant="link" onClick={handleMasterMenuClick}>
            {merchants[masterMerchantId]?.name}: {masterMenuName}
          </Button>
          .
        </>
      );
    }

    return 'Adding merchants here will make this menu a master. Linked merchants will receive a cloned menu with limited functionality. Removing a merchant also removes their cloned menu. Contact support for assistance.';
  })();

  return (
    <Stack maw={600}>
      <Stack gap="xs2">
        <MerchantTreeSelect
          selectedMerchants={selectedLinkedMerchants}
          setSelectedMerchants={setSelectedLinkedMerchants}
          disabled={disableMerchantSelector}
          menu={menu}
        />
        {shouldShowMasterMenuDisabledTooltip && (
          <Text variant="sublabel-xs">{getMasterMenuDisabledTooltip()}</Text>
        )}
      </Stack>
      <Alert c="var(--content-primary)" variant="gray" icon={<BellIconMd />} mt="xs2">
        <Text>{alertMessage}</Text>
      </Alert>
    </Stack>
  );
}

LinkedMerchants.propTypes = {
  menu: PropTypes.shape({
    associatedMerchants: PropTypes.arrayOf(
      PropTypes.shape({
        merchantId: PropTypes.string,
        merchantName: PropTypes.string,
        merchantCompanyId: PropTypes.string,
        merchantCompanyName: PropTypes.string,
      }),
    ),
    menuHasSharedCategories: PropTypes.bool,
    menuSource: PropTypes.string,
    clonedMenu: PropTypes.bool,
    masterMerchant: PropTypes.shape({
      menuUuid: PropTypes.string,
      menuId: PropTypes.string,
      merchantId: PropTypes.string,
      menuName: PropTypes.string,
    }),
    masterMenuPermissionTier: PropTypes.string,
  }),
  selectedLinkedMerchants: PropTypes.arrayOf(
    PropTypes.shape({
      merchantId: PropTypes.string,
      merchantName: PropTypes.string,
      merchantCompanyId: PropTypes.string,
      merchantCompanyName: PropTypes.string,
    }),
  ),
  setSelectedLinkedMerchants: PropTypes.func,
};
